/* eslint-disable import/no-named-as-default-member */
/* eslint-disable @typescript-eslint/no-explicit-any */
import dayjs from 'dayjs'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { useState, useEffect } from 'react'

import {
  filteredData,
  processFormParticipantsData,
  processSummaryTshirtCount,
} from './utils'
import {
  FormParticipant,
  Participant as ParticipantApi,
  FormParticipantsByFormEventIdQuery,
  ParticipantsByEventIdQuery,
} from '../API'
import {
  formParticipantsByFormEventId,
  participantsByEventId,
} from '../api/queries'
import { fetchQuery } from '../utils/ApiUtils'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.tz.setDefault('Asia/Manila')
dayjs.extend(isSameOrBefore)

type Participant = Omit<Exclude<ParticipantApi, null>, '__typename'>

export const listParticipantsByEvent = async ({
  eventId = '',
  isFree = false, // ONSITE REGISTRATION
  isFormParticipant = false, // FORM PARTICIPANT
  startTimestamp = '',
}) => {
  let nextNextToken = ''
  let nextNextShirtTotal = {}
  let hasNext = true
  let data = []

  const formEventCategories = {}
  const usedTokens = []

  const fetchData = async (currentToken, currentShirtTotal) => {
    try {
      const { items, nextToken: newNextToken } = (await fetchQuery(
        !isFormParticipant
          ? 'participantsByEventId'
          : 'formParticipantsByFormEventId',
        !isFormParticipant
          ? participantsByEventId
          : formParticipantsByFormEventId,
        {
          ...(!isFormParticipant
            ? {
                eventId,
                ...(!isFree && {
                  filter: { orderId: { attributeExists: true } },
                }),
              }
            : {
                formEventId: eventId,
              }),
          ...(currentToken !== null &&
            currentToken.length > 0 && {
              nextToken: currentToken,
            }),
        },
      )) as unknown as {
        items: Participant[] | FormParticipant[]
        nextToken: string
      }

      if (currentToken !== null && currentToken.length > 0)
        usedTokens.push(currentToken)

      let newItems = [],
        shirtTotal = {}
      if (isFormParticipant) {
        ;(items[0] as FormParticipant).formEvent?.categories?.items?.forEach(
          (a) => {
            formEventCategories[a.id] = a.name
          },
        )

        const processedData = processFormParticipantsData(
          items,
          currentShirtTotal,
          formEventCategories,
        )
        newItems = processedData.items
        shirtTotal = processedData.tshirtTotal
      } else {
        // filter values
        newItems = filteredData(items, startTimestamp, isFree)
      }

      return {
        items: newItems,
        nextToken: newNextToken,
        nextShirtTotal: shirtTotal,
      }
    } catch (e) {
      console.log(e)
      if ('data' in e) {
        const data:
          | ParticipantsByEventIdQuery
          | FormParticipantsByFormEventIdQuery = e.data

        const newItems = filteredData(
          data[
            !isFormParticipant
              ? 'participantsByEventId'
              : 'formParticipantsByFormEventIdQuery '
          ]?.items,
          startTimestamp,
          isFree,
        )

        return {
          items: newItems,
          nextToken:
            data[
              !isFormParticipant
                ? 'participantsByEventId'
                : 'formParticipantsByFormEventIdQuery '
            ].nextToken,
        }
      }
    }
  }
  while (hasNext === true) {
    const { items, nextToken, nextShirtTotal } = await fetchData(
      nextNextToken,
      nextNextShirtTotal,
    )
    data = [...data, ...items]
    nextNextToken = nextToken
    nextNextShirtTotal = nextShirtTotal
    if (nextToken === null) hasNext = false
  }

  const tshirtData = processSummaryTshirtCount(
    nextNextShirtTotal,
    formEventCategories,
  )
  return { shirtTotal: tshirtData, participants: data }
}

export const useListParticipantsByEvent = ({
  eventId = '',
  isFree = false, // ONSITE REGISTRATION
  isFormParticipant = false, // FORM PARTICIPANT
  startTimestamp = '',
}) => {
  const [participants, setParticipants] = useState<
    Participant[] | FormParticipant[]
  >([])
  const [formParticipantTshirtTotal, setFormParticipantTshirtTotal] =
    useState(null)
  const [loading, setLoading] = useState(false)
  const error = null

  useEffect(() => {
    let didCancel = false

    const fetchData = async () => {
      if (didCancel) return
      setLoading(true)

      if (eventId) {
        const results = await listParticipantsByEvent({
          eventId,
          isFree,
          isFormParticipant,
          startTimestamp,
        })
        setParticipants(results.participants)
        setFormParticipantTshirtTotal(results.shirtTotal)

        setLoading(false)
      }
    }

    fetchData()
    return () => {
      didCancel = true
    }
  }, [eventId, startTimestamp])

  return {
    participants,
    formParticipantTshirtTotal,
    apiLoading: loading,
    apiError: error,
  }
}
